import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { TextArea } from "@/components/text-area/TextArea";
import { EstadosPlanificacionProductiva } from "@/constants/enums/status/entity-status-ids";
import { AppRoutes } from "@/constants/routes";
import { ProductPlanificationStatus } from "@/constants/entity-status";
import { parseTonInputToNumber } from "@/utils/query-string";
import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";
import { ProductPlanificationCultivoCard } from "../create/ProductPlanificationCultivoCard";
import type { GranoProductivo } from "@/services/productive-entities.service";
import {
  fetchStaticOptions,
  fetchZonas,
  getAccountOptions,
  calculatePrefilledValues,
  loadOptionsHandler,
} from "../schema/aux-functions";
import {
  fetchPPDetailHandler,
  updateProductPlanificationHandler,
} from "../schema/handler-functions";
import { PPMainDataForm } from "../components/card/PPMainDataForm";
import {
  type ProductPlanificationCreationForm,
} from "../schema/product-planification.schema";
import { HADifferenceModal, type HADifferenceItem } from "../create/HADifferenceModal";
import { ArrowLeft } from "lucide-react";

export const ProductPlanificationEditPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();
  const { id } = useParams();
  const idNum = Number(id);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ProductPlanificationCreationForm>({
    defaultValues: {
      account: null,
      idCampana: [],
      idZonaProductiva: [],
      haPropias: "",
      haAlquiladas: "",
      detalle: "",
      cultivos: [
        {
          idGrano: [],
          hectareasAsembrar: "",
          fechaEstimadaSiembra: undefined,
          consumoPropio: "",
          consumoPropioPicado: "",
          costoEstimado: "",
          toneladasRinde: "",
        },
      ],
    },
  });

  const watched = useWatch({ control });

  const watchedCultivos = useWatch({ control, name: "cultivos" });
  const watchedCampana = useWatch({ control, name: "idCampana" });

  const {
    fields: cultivoFields,
    append: appendCultivo,
    remove: removeCultivo,
    replace: replaceCultivos,
  } = useFieldArray({ control, name: "cultivos" });

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [detail, setDetail] = useState<any>(null);
  const [cuentaUsuarios, setCuentaUsuarios] = useState<any[]>([]);
  const [loadingCuentaUsuarios, setLoadingCuentaUsuarios] = useState(false);

  const [campanaOptions, setCampanaOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [granoOptions, setGranoOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [granosProductivos, setGranosProductivos] = useState<GranoProductivo[]>(
    [],
  );
  const [zonaOptions, setZonaOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [openHADifferenceModal, setOpenHADifferenceModal] = useState(false);
  const [motivosDesvio, setMotivosDesvio] = useState<HADifferenceItem[]>([]);

  const isPresented = useMemo(() => {
    const idEstado = Number(detail?.idEstado);
    if (idEstado) {
      return idEstado === EstadosPlanificacionProductiva.Presentada;
    }
    return detail?.estado === ProductPlanificationStatus.PRESENTED;
  }, [detail]);

  const isLoaded = useMemo(() => {
    const idEstado = Number(detail?.idEstado);
    if (idEstado) {
      return idEstado === EstadosPlanificacionProductiva.Cargada;
    }
    return detail?.estado === ProductPlanificationStatus.LOADED;
  }, [detail]);

  const isInReview = useMemo(() => {
    const idEstado = Number(detail?.idEstado);
    if (idEstado) {
      return idEstado === EstadosPlanificacionProductiva.DatosEnRevision;
    }
    return detail?.estado === ProductPlanificationStatus.IN_REVISION;
  }, [detail]);

  const loadAccountOptions = (inputValue: string) =>
    loadOptionsHandler({
      timeoutRef,
      inputValue,
      setLoadingPersons: setLoadingAccount,
      getOptions: getAccountOptions,
    });

  const handleCancel = () => {
    navigate(AppRoutes.PRODUCT_PLANIFICATION);
  };

  const handleAddCultivo = () => {
    appendCultivo({
      idGrano: [],
      hectareasAsembrar: "",
      fechaEstimadaSiembra: undefined,
      consumoPropio: "",
      consumoPropioPicado: "",
      costoEstimado: "",
      toneladasRinde: "",
    });
  };

  const { open: confirmModalOpen, setOpen: setConfirmModalOpen } = useDisclosure();

  const selectedZonaComercial = useMemo(() => {
    return (watched?.account as any)?.zonaComercial as string | undefined;
  }, [watched?.account]);

  const lastMbzKeyByIndexRef = useRef<Record<number, string>>({});

  const granosById = useMemo(() => {
    return (granosProductivos ?? []).reduce<Record<number, GranoProductivo>>(
      (acc, g) => {
        acc[g.id] = g;
        return acc;
      },
      {},
    );
  }, [granosProductivos]);

  const hasDuplicatedGranos = false;

  const granoOptionsByIndex = useMemo(() => {
    return (watchedCultivos ?? []).map(() => {
      return (granoOptions ?? []).map((o) => ({
        ...o,
        disabled: false,
      }));
    });
  }, [watchedCultivos, granoOptions]);

  const showHADifferenceButton = useMemo(() => {
    const haPropias = parseTonInputToNumber(watched?.haPropias) ?? 0;
    const haAlq = parseTonInputToNumber(watched?.haAlquiladas) ?? 0;
    const sumaCultivos = (watched?.cultivos ?? []).reduce((acc: number, c) => {
      const ha = parseTonInputToNumber((c as any)?.hectareasAsembrar) ?? 0;
      return acc + ha;
    }, 0);
    const hasAnyCultivoHa = (watched?.cultivos ?? []).some((c) => {
      const ha = parseTonInputToNumber((c as any)?.hectareasAsembrar) ?? 0;
      return ha > 0;
    });
    const diff = haPropias + haAlq - sumaCultivos;
    if (!Number.isFinite(diff)) return false;
    if (diff === 0) return false;
    return hasAnyCultivoHa;
  }, [watched?.haPropias, watched?.haAlquiladas, watched?.cultivos]);

  const haDifferenceAbs = useMemo(() => {
    const haPropias = parseTonInputToNumber(watched?.haPropias) ?? 0;
    const haAlq = parseTonInputToNumber(watched?.haAlquiladas) ?? 0;
    const sumaCultivos = (watched?.cultivos ?? []).reduce((acc: number, c) => {
      const ha = parseTonInputToNumber((c as any)?.hectareasAsembrar) ?? 0;
      return acc + ha;
    }, 0);
    return Math.abs(haPropias + haAlq - sumaCultivos);
  }, [watched?.haPropias, watched?.haAlquiladas, watched?.cultivos]);

  const haDifference = useMemo(() => {
    const haPropias = parseTonInputToNumber(watched?.haPropias) ?? 0;
    const haAlq = parseTonInputToNumber(watched?.haAlquiladas) ?? 0;
    const sumaCultivos = (watched?.cultivos ?? []).reduce((acc: number, c) => {
      const ha = parseTonInputToNumber((c as any)?.hectareasAsembrar) ?? 0;
      return acc + ha;
    }, 0);
    return haPropias + haAlq - sumaCultivos;
  }, [watched?.haPropias, watched?.haAlquiladas, watched?.cultivos]);

  const haDifferenceMotivosValid = useMemo(() => {
    if (!showHADifferenceButton) return true;
    const sumMotivos = (motivosDesvio ?? []).reduce(
      (acc, m) => acc + (m?.valorHectareas ?? 0),
      0,
    );
    return haDifferenceAbs > 0 && Math.abs(sumMotivos - haDifferenceAbs) < 0.00001;
  }, [showHADifferenceButton, motivosDesvio, haDifferenceAbs]);

  const disableAdd = useMemo(() => {
    if (loadingDetail) return true;
    if (loadingUpdate) return true;
    if (isPresented) return true;

    const cultivos = (watched?.cultivos ?? []) as any[];
    const cultivosValid =
      cultivos.length > 0 &&
      cultivos.every((c) => {
        const ha = parseTonInputToNumber(c?.hectareasAsembrar) ?? 0;
        return ha > 0 && Boolean(c?.fechaEstimadaSiembra);
      });

    return !cultivosValid || !haDifferenceMotivosValid || hasDuplicatedGranos;
  }, [
    haDifferenceMotivosValid,
    isPresented,
    loadingDetail,
    loadingUpdate,
    watched?.cultivos,
    hasDuplicatedGranos,
  ]);

  const onSubmit = async (values: ProductPlanificationCreationForm) => {
    if (!detail) return;
    await updateProductPlanificationHandler({
      values,
      detail,
      showHADifferenceButton,
      motivosDesvio,
      setLoadingUpdate,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      void fetchStaticOptions({
        setLoadingOptions,
        setCampanaOptions,
        setGranoOptions,
        setGranosProductivos,
      });
    }
  }, [isActive]);

  // Prefill costoEstimado y toneladasRinde por cultivo (según MBZ)
  useEffect(() => {
    const idCampana = (watchedCampana ?? [])?.[0];
    const idZonaProductiva = (watched?.idZonaProductiva ?? [])?.[0];
    if (!idCampana || !idZonaProductiva) return;

    calculatePrefilledValues({
      watchedCultivos,
      setValue,
      idCampana,
      idZonaProductiva,
      lastMbzKeyByIndexRef,
      granosById,
    });
  }, [watchedCampana, watched?.idZonaProductiva, watchedCultivos, setValue, granosById]);

  useEffect(() => {
    if (isActive && id) {
      void fetchPPDetailHandler({
        id: idNum,
        setLoadingDetail,
        setLoadingCuentaUsuarios,
        setDetail,
        setCuentaUsuarios,
        setMotivosDesvio,
        setValue,
        replaceCultivos,
        navigate,
        fetchPermissions,
      });
    }
  }, [id, idNum, isActive]);

  useEffect(() => {
    if (isActive) {
      void fetchZonas({ zonaComercial: selectedZonaComercial, setZonaOptions });
    }
  }, [selectedZonaComercial, isActive]);

  const loadingAllData = useMemo(() => {
    return loadingDetail || loadingCuentaUsuarios || loadingAccount || loadingOptions;
  }, [loadingDetail, loadingCuentaUsuarios, loadingAccount, loadingOptions]);

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      height="100%"
      minHeight={0}
      data-testid="product-planification-edit-page"
    >
      <SectionHeader
        title="Planificación productiva"
        breadcrumbComponent={!loadingDetail &&
          <Flex
            alignItems="center"
            gap="10px"
            position="absolute"
            left="50%"
            transform="translateX(-50%)"
          >
            <Text fontWeight={600} fontSize="16px">
              Estado:
            </Text>
            <RequestStatusBadge
              status={detail?.estado as ProductPlanificationStatus}
              noPaddingY
            />
          </Flex>
        }
        addButtonLabel="Presentar"
        cancelButtonLabel="Volver"
        cancelButtonIcon={<ArrowLeft />}
        handleCancel={handleCancel}
        handleAdd={() => setConfirmModalOpen(true)}
        disableAdd={disableAdd}
        loading={loadingUpdate || loadingDetail}
      />

      <Flex flex="1" minHeight={0} overflowY="auto" flexDir="column" gap="16px">
        <PPMainDataForm
          errors={errors}
          control={control}
          watched={watched}
          cuentaUsuarios={cuentaUsuarios}
          loadingAccount={loadingAccount}
          loadAccountOptions={loadAccountOptions}
          loadingCreate={loadingUpdate || loadingDetail || isPresented}
          loadingCuentaUsuarios={loadingCuentaUsuarios}
          loadingOptions={loadingOptions}
          campanaOptions={campanaOptions as any}
          zonaOptions={zonaOptions as any}
          loadingZonas={loadingDetail}
          showHADifferenceButton={showHADifferenceButton && !loadingAllData}
          haDifferenceMotivosValid={haDifferenceMotivosValid}
          haDifferenceAbs={haDifferenceAbs}
          motivosDesvio={motivosDesvio}
          setOpenHADifferenceModal={setOpenHADifferenceModal}
          readOnlyMainFields={isPresented || isLoaded || isInReview}
        />

        {(cultivoFields ?? []).map((f, index) => (
          <ProductPlanificationCultivoCard
            key={f.id}
            index={index}
            control={control as any}
            granoOptions={(granoOptionsByIndex?.[index] ?? granoOptions) as any}
            disableGrano={isPresented}
            cultivosFieldsLength={cultivoFields.length}
            loadingCreate={loadingUpdate || loadingDetail || isPresented}
            removeCultivo={removeCultivo}
            handleAddCultivo={handleAddCultivo}
            hideAddButton
            hideDeleteButton
          />
        ))}

        <Flex flexDir="column" gap="12px" paddingBottom="16px">
          <Text fontWeight={700} fontSize="20px">
            Detalle
          </Text>
          <TextArea
            errors={errors as any}
            inputName="detalle"
            maxLength={50}
            register={register as any}
            placeholder="Ingresar detalle"
            helperText="Máximo 50 caracteres"
            disabled={loadingUpdate || loadingDetail || isPresented}
          />
        </Flex>
      </Flex>

      <HADifferenceModal
        open={openHADifferenceModal}
        setOpen={setOpenHADifferenceModal}
        differenceHectares={haDifference}
        value={motivosDesvio}
        onChange={(next) => setMotivosDesvio(next)}
      />

      <Suspense>
        <ConfirmModalDynamic
          openConfirm={confirmModalOpen}
          setOpenConfirm={setConfirmModalOpen}
          title="Presentar planificación"
          subtitle="Al presentar la planificación, el productor visualizará  los datos modificados en la App Mobile. ¿Desea continuar?"
          handleAccept={handleSubmit(onSubmit)}
          loadingAccept={loadingUpdate}
        />
      </Suspense>
    </Flex>
  );
};


const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
